// src/components/customers/tabs/tabs-OrderHistory.jsx
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrdersQuery, useOrderStatusesQuery } from "@queries/order";
import api from "@services/api";

export default function OrderHistoryTab({ customer }) {
  const id = customer?.customer_id || customer?.id;
  const qc = useQueryClient();
  const { data, isLoading, isError } = useOrdersQuery({ customerId: id }, 1, 1000);
  const { data: statuses = [] } = useOrderStatusesQuery(1);

  const orders = data?.data || [];

  // "Complete" status id — resolved from the live status list, falling back to 5
  // (the canonical Complete id) if the list hasn't loaded.
  const completeStatusId =
    statuses.find((s) => String(s.name).toLowerCase() === "complete")?.order_status_id ?? 5;

  // ---- Bulk selection state ----
  const [selected, setSelected] = React.useState(() => new Set());
  const [noteText, setNoteText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [progress, setProgress] = React.useState("");
  // WN-0152: the bulk status action is a dropdown of ALL order statuses (not a
  // fixed "Complete" button). Defaults to Complete for convenience.
  const [bulkStatus, setBulkStatus] = React.useState("");

  // Drop selections that no longer exist after a refetch.
  React.useEffect(() => {
    setSelected((prev) => {
      const ids = new Set(orders.map((o) => o.order_id));
      const next = new Set([...prev].filter((x) => ids.has(x)));
      return next.size === prev.size ? prev : next;
    });
  }, [orders]);

  const allSelected = orders.length > 0 && selected.size === orders.length;
  const someSelected = selected.size > 0;

  const toggleOne = (orderId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(orderId) ? next.delete(orderId) : next.add(orderId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === orders.length ? new Set() : new Set(orders.map((o) => o.order_id))
    );
  };

  const refresh = () => qc.invalidateQueries({ queryKey: ["orders"] });

  // Run an async action over every selected order, sequentially (the dev server
  // is single-threaded), with a simple progress readout and error tally.
  const runBulk = async (label, fn) => {
    const ids = [...selected];
    if (ids.length === 0 || busy) return;
    setBusy(true);
    let ok = 0;
    const failed = [];
    for (let i = 0; i < ids.length; i++) {
      setProgress(`${label}: ${i + 1}/${ids.length}…`);
      try {
        await fn(ids[i]);
        ok++;
      } catch (e) {
        failed.push(ids[i]);
      }
    }
    setBusy(false);
    setProgress("");
    refresh();
    if (failed.length) {
      alert(`${label}: ${ok} updated, ${failed.length} failed (orders: ${failed.join(", ")}).`);
    } else {
      alert(`${label}: all ${ok} order(s) updated.`);
    }
  };

  // Default the dropdown to Complete once statuses load (keeps prior convenience).
  React.useEffect(() => {
    if (!bulkStatus && completeStatusId) setBulkStatus(String(completeStatusId));
  }, [completeStatusId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkApplyStatus = () => {
    if (!bulkStatus) { alert("Pick a status first."); return; }
    const statusName = statuses.find((s) => String(s.order_status_id) === String(bulkStatus))?.name || "the selected status";
    if (!window.confirm(`Change status to "${statusName}" for ${selected.size} selected order(s)?`)) return;
    runBulk(`Set ${statusName}`, (orderId) =>
      api.post(`/order/${orderId}/status`, {
        status: Number(bulkStatus),
        comment: "",
        notify: 0,
        override: false,
      })
    );
  };

  const bulkAddNote = () => {
    const text = noteText.trim();
    if (!text) {
      alert("Type a note first.");
      return;
    }
    if (!window.confirm(`Add this note to ${selected.size} selected order(s)?`)) return;
    runBulk("Add note", (orderId) =>
      api.post(`/order/${orderId}/notes`, { comment: text })
    ).then(() => setNoteText(""));
  };

  // WN-0150 — download a branded PDF account statement (outstanding orders).
  const [reportBusy, setReportBusy] = React.useState(false);
  const downloadCollectionReport = async () => {
    if (!id || reportBusy) return;
    setReportBusy(true);
    try {
      const res = await api.get(`/customers/${id}/collection-report`, {
        responseType: "blob",
      });
      // Filename comes from the server's Content-Disposition; fall back sensibly.
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const fname = m ? m[1] : `collection_report_${id}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Could not generate the collection report. Please try again.");
    } finally {
      setReportBusy(false);
    }
  };

  const getStatusName = (statusIdOrName) => {
    if (!statusIdOrName) return "N/A";
    // If it's already a string name (not a number string), return it
    if (isNaN(Number(statusIdOrName))) return statusIdOrName;

    const status = statuses.find(s => String(s.order_status_id) === String(statusIdOrName));
    return status ? status.name : statusIdOrName;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  if (isLoading) return <div className="oc-empty">Loading order history...</div>;
  if (isError) return <div className="oc-empty" style={{ color: "red" }}>Error loading order history</div>;

  return (
    <div className="oc-card">
      <div className="oc-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Orders History</h2>
        <button
          type="button"
          className="oc-btn primary"
          onClick={downloadCollectionReport}
          disabled={reportBusy}
          title="Download a PDF account statement of this customer's outstanding orders (Cleaning/Delivery)"
        >
          {reportBusy ? "Generating…" : "Generate Collection Report"}
        </button>
      </div>

      {/* Bulk action bar — appears once at least one order is selected. */}
      {someSelected && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
            padding: "10px 12px",
            margin: "8px 0",
            background: "#eef2ff",
            border: "1px solid #c7d2fe",
            borderRadius: 8,
          }}
        >
          <strong>{selected.size} selected</strong>
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <span>Change status to</span>
            <select
              className="form-control"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              disabled={busy}
              style={{ width: "auto", minWidth: 150 }}
            >
              <option value="" disabled>Select status…</option>
              {statuses.map((s) => (
                <option key={s.order_status_id} value={s.order_status_id}>{s.name}</option>
              ))}
            </select>
            <button type="button" className="oc-btn primary" onClick={bulkApplyStatus} disabled={busy || !bulkStatus}>
              Apply
            </button>
          </span>
          <span style={{ width: 1, height: 24, background: "#c7d2fe" }} />
          <input
            type="text"
            className="form-control"
            placeholder="Note to apply to all selected…"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            style={{ minWidth: 240, flex: "1 1 240px" }}
            disabled={busy}
          />
          <button type="button" className="oc-btn" onClick={bulkAddNote} disabled={busy}>
            Add note
          </button>
          <button
            type="button"
            className="oc-btn"
            onClick={() => setSelected(new Set())}
            disabled={busy}
          >
            Clear
          </button>
          {progress && <span style={{ color: "#4338ca" }}>{progress}</span>}
        </div>
      )}

      <div className="oc-table-wrap">
        <table className="oc-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={toggleAll}
                  aria-label="Select all orders"
                />
              </th>
              <th>Name</th>
              <th>Order ID</th>
              <th>Order Total</th>
              <th>Order Date</th>
              <th>Order Status</th>
              <th>Order Type</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 24 }}>
                  No results!
                </td>
              </tr>
            ) : (
              orders.map((o) => {
                const complementaryReason = o.complemantary_reason ?? o.order?.complemantary_reason ?? "";
                const rowStyle = String(complementaryReason || "").trim()
                  ? { backgroundColor: "#efd9d9" }
                  : undefined;

                return (
                <tr key={o.order_id} style={rowStyle}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(o.order_id)}
                      onChange={() => toggleOne(o.order_id)}
                      aria-label={`Select order ${o.order_id}`}
                    />
                  </td>
                  <td>{(typeof o.customer === 'string' ? o.customer : (o.firstname || customer?.name || "N/A"))}{o.invoice_label ? ` (${o.invoice_label})` : ""}</td>
                  <td>
                    <a href={`/orders/${o.order_id}`} target="_blank" rel="noreferrer">
                      {o.order_id}
                    </a>
                  </td>
                  <td>
                    {Number.isFinite(o.order_total)
                      ? Number(o.order_total).toFixed(2)
                      : (o.total ? Number(o.total).toFixed(2) : "0.00")}
                  </td>
                  <td>{formatDate(o.date_added)}</td>
                  <td>{getStatusName(o.order_status_id || o.order_status || o.status)}</td>
                  <td>{o.type || o.order_type || "N/A"}</td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
